import React, { useState, useMemo, useEffect } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import CostModelProjectFormCst from "../CostModelProjectFormCst";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useAuth } from "@context/auth";

const EditCostModelProject: React.FC = () => {
  const { idProject, id, idCostModel } = useParams();
  const { t } = useTranslation(["projects-cost-model-page"]);
  const navigate = useNavigate();
  const [options, setOptions] = useState<any[]>([]);
  const [openOutcome, setOpenOutcome] = useState(false);
  const { user } = useAuth();

  /*########### ROLE PERMISSION ###########*/

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  /*#######################################*/

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getCostModel.replace(":budgetProjectId", String(idProject))}`,
    idCostModel,
    PagesID["projects.cost-model"],
    `corporateId=${id}`,
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.cost-model.messageLoading"),
        title: t("edit.outcome.cost-model.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.cost-model.messageLoading"),
      title: t("edit.outcome.cost-model.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerCostModel } = useSWRMutation(
    `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}/${idCostModel}?corporateId=${id}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  /*Get the project information*/
  const { dataDetail: dataProject } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Get the account list for create the autocomplete options*/
  const { dataDetail: dataAccountList } = useFetchDataDetail(
    `${ServicesURL.getBankAccountListAll}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Update the options for the autocomplete field*/
  useEffect(() => {
    if (!dataAccountList) {
      setOptions([]);
      return;
    }
    setOptions(
      dataAccountList.flatMap((item: any) =>
        item.descriptions.map((desc: any) => ({
          value: desc.id,
          label: `${item.sap_number} - ${desc.title}`,
        })),
      ),
    );
  }, [dataAccountList]);

  /*Manage the columns for the form*/
  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "account_description_id") {
        return { ...el, options: options || [] };
      }

      return { ...el };
    });
  }, [columns, options]);

  /*Define wich columns of the project are visibile like info*/
  const filteredDto = useMemo(() => {
    if (!dataProject?.dto || !dataDetail?.dto) return {};

    return {
      code: dataProject.dto.code,
      title: dataProject.dto.title,
      cost_center: dataProject.dto.cost_center.sap_code,
      project_allocated_budget: dataProject.dto.allocated_budget,
      project_residual_budget: dataProject.dto.residual_budget,
      ...dataDetail.dto,
    };
  }, [dataProject, dataDetail]);

  /*Submit of the form for create a new cost model*/
  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    const objToSend: any = {
      account_description_id:
        inputForm.account_description_id?.value ||
        inputForm.account_description_id,
      allocated_budget: inputForm.allocated_budget || null,
      type: inputForm.type || null,
    };

    try {
      await triggerCostModel(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.cost-model.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-cost-centers.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("add.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <CostModelProjectFormCst
              row={filteredDto || []}
              columns={getColumns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(
                  `/corporate/${id}/detail/budget-management/projects/edit/${idProject}`,
                  { state: { tab: "cost-model" } },
                );
              }}
              fullWidth={true}
              readOnly={
                !!(isPM && dataProject?.dto?.status !== "UNDER_DEVELOPMENT")
              }
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              `/corporate/${id}/detail/budget-management/projects/edit/${idProject}`,
              { state: { tab: "cost-model" } },
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditCostModelProject;
